import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Table, Card, Button, Spin, Alert, Badge, Typography, Result } from "antd";
import { LeftOutlined, ReloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { getOrderItemsByOrderId } from "../../services/orderItemService";
import { getOrderById } from "../../services/orderService";
import { useApiCall } from "../../hooks/useApiCall";
import { formatPrice, formatTotalPrice } from "../../utils/formatters";
import { getStatusInfo } from "../../constants/statusOptions";

const { Title } = Typography;

const OrderItemsPage = () => {
  const { orderId } = useParams();
  const [orderItems, setOrderItems] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);
  
  // Use separate hooks for different API calls
  const { loading: orderItemsLoading, error: orderItemsError, execute: executeOrderItemsCall } = useApiCall();
  const { loading: orderDetailsLoading, error: orderDetailsError, execute: executeOrderDetailsCall } = useApiCall();

  const fetchOrderData = async () => {
    if (!orderId) {
      return;
    }

    try {
      // Fetch both in parallel but handle errors separately
      const [itemsResponse, orderResponse] = await Promise.all([
        executeOrderItemsCall(() => getOrderItemsByOrderId(orderId)).catch(err => {
          console.error("Error fetching order items:", err);
          return { content: [] }; // Return empty array on error
        }),
        executeOrderDetailsCall(() => getOrderById(orderId)).catch(err => {
          console.error("Error fetching order details:", err);
          return null; // Return null on error
        })
      ]);

      setOrderItems(itemsResponse?.content || []);
      setOrderDetails(orderResponse || null);
    } catch (err) {
      console.error("Error fetching order data:", err);
    }
  };

  useEffect(() => {
    fetchOrderData();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    fetchOrderData();
  };

  const getStatusBadge = (status) => {
    const { color, text } = getStatusInfo(status, "orderItem");
    return <Badge status={color} text={text} />;
  };
  
  const columns = [
    {
      title: "Kalem Adı",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Adet",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Adet Fiyatı",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (value) => formatPrice(value),
    },
    {
      title: "Toplam",
      key: "total",
      render: (_, record) => formatTotalPrice(record.quantity, record.unitPrice),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: getStatusBadge,
    },
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (value) => value || "-",
    },
  ];

  const totalAmount = orderItems.reduce(
    (sum, item) => {
      try {
        return sum + item.quantity * (item.unitPrice || 0);
      } catch (error) {
        return sum;
      }
    },
    0
  );

  // Handle invalid order ID
  if (!orderId) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="warning"
          title="Geçersiz Sipariş"
          subTitle="Sipariş ID'si bulunamadı."
          extra={
            <Link to="/orders">
              <Button type="primary" icon={<LeftOutlined />}>
                Siparişlerime Dön
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  // Handle major errors when both calls fail
  if (orderItemsError && orderDetailsError && !orderItemsLoading && !orderDetailsLoading) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="error"
          title="Sipariş Yüklenemedi"
          subTitle="Sipariş detayları yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
          extra={[
            <Button type="primary" key="retry" icon={<ReloadOutlined />} onClick={handleRetry}>
              Tekrar Dene
            </Button>,
            <Link to="/orders" key="back">
              <Button icon={<LeftOutlined />}>
                Siparişlerime Dön
              </Button>
            </Link>
          ]}
        />
      </div>
    );
  }

  // Show loading state
  if (orderItemsLoading || orderDetailsLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: "5rem" }}>
        <Spin tip="Sipariş detayları yükleniyor..." size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Title level={4} style={{ marginBottom: 0 }}>
                Sipariş Detayları - #{orderId}
              </Title>
              {orderDetails && (
                <div>
                  {getStatusBadge(orderDetails.status)}
                </div>
              )}
              {orderDetailsError && (
                <Alert
                  message="Sipariş bilgileri yüklenemedi"
                  type="warning"
                  showIcon
                  size="small"
                  style={{ margin: 0 }}
                />
              )}
            </div>
            <Link to="/orders">
              <Button icon={<LeftOutlined />}>Siparişlerime Dön</Button>
            </Link>
          </div>
        }
        bordered={false}
      >
        {/* Show warning if order items failed to load */}
        {orderItemsError && (
          <Alert
            message="Sipariş kalemleri yüklenemedi"
            description="Sipariş kalemi bilgileri yüklenirken bir hata oluştu."
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={handleRetry}>
                Tekrar Dene
              </Button>
            }
          />
        )}

        {orderItems.length === 0 && !orderItemsError ? (
          <Alert 
            message="Bu siparişe ait kalem bulunamadı" 
            description="Bu sipariş henüz kalem içermiyor."
            type="info" 
            showIcon 
          />
        ) : orderItems.length > 0 ? (
          <>
            <Table
              dataSource={orderItems}
              columns={columns}
              rowKey="id"
              pagination={false}
              scroll={{ x: true }}
              footer={() => (
                <div style={{ textAlign: "right", fontWeight: "bold" }}>
                  Toplam: {formatPrice(totalAmount)}
                </div>
              )}
            />
          </>
        ) : null}

        {/* Show partial success info */}
        {orderDetails && orderItemsError && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
            <Typography.Text type="secondary">
              <ExclamationCircleOutlined style={{ marginRight: 8 }} />
              Sipariş bilgileri yüklendi ancak kalemler yüklenemedi. Sayfayı yenilemeyi deneyin.
            </Typography.Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OrderItemsPage;